/**
 * Which carrier should take a parcel, from its boxes, its postcode and whether
 * the customer has a printer.
 *
 * One ladder for two flows. Trade-in collections (frontend, `assignCarrier` in
 * `frontend/src/utils/carrierAssignment.js`) and customer returns (the case page
 * route in `workers/src/index.js`) both ask this module. A second copy would
 * drift, and a carrier is not cosmetic: it picks the customer's email and the
 * tracking link.
 *
 * This module is pure. It takes boxes already measured in kg and cm. Parsing a
 * HubSpot `package_details` string or a Shopify address is the caller's job.
 */

// Royal Mail: a single parcel, 10kg mainland and 20kg to Northern Ireland,
// within 61x46x46cm on its sorted sides.
const ROYAL_MAIL_MAX_WEIGHT_KG = 10
const ROYAL_MAIL_NI_MAX_WEIGHT_KG = 20
const ROYAL_MAIL_MAX_DIMS_CM = [61, 46, 46]

// Past Royal Mail's longest permitted side, Royal Mail is out of the question
// however light the parcel is. That is all "oversized" means here.
const OVERSIZED_LENGTH_CM = ROYAL_MAIL_MAX_DIMS_CM[0]

// ParcelForce: up to 30kg, longest side up to 150cm.
const PARCELFORCE_MAX_WEIGHT_KG = 30
const PARCELFORCE_MAX_LENGTH_CM = 150

// Royal Mail will hand a 10-20kg sender a QR code to be scanned in branch, so
// a customer with no printer can still use them.
const QR_CODE_MAX_WEIGHT_KG = 20

// Beyond these, no parcel carrier will take it and it goes out to bid on Shiply.
const SHIPLY_MIN_WEIGHT_KG = 70
const SHIPLY_MIN_LENGTH_CM = 150

/**
 * Pick a carrier.
 *
 * @param {object} shipment
 * @param {{weight: number, length: number, width: number, height: number}[]} shipment.boxes
 *   kg and cm. An empty list means "nothing known": the answer is a guess.
 * @param {boolean} shipment.hasPrinter - can the customer print a label?
 * @param {string|null} [shipment.postcode] - a UK postcode. Null or blank means mainland.
 * @param {string|null} [shipment.sendType] - 'dropoff' or 'drop-off' means the
 *   customer carries it to a branch. Anything else is a courier collection.
 * @returns {{carrierId: string, reason: string, notAllowed?: boolean}}
 */
export function routeShipment({ boxes, hasPrinter, postcode = null, sendType = null }) {
    return route(measureShipment({ boxes, hasPrinter, postcode, sendType }))
}

/** True for a Northern Ireland postcode (BT). */
export function isNorthernIrelandPostcode(postcode) {
    if (!postcode) return false
    return postcode.toUpperCase().replace(/\s/g, '').substring(0, 2) === 'BT'
}

/** Everything the ladder below needs to know about a shipment, derived once. */
function measureShipment({ boxes, hasPrinter, postcode, sendType }) {
    const isNorthernIreland = isNorthernIrelandPostcode(postcode)

    // With no boxes there are no dimensions to verify, and unverified must not
    // read as "fits". That would route a wardrobe by Royal Mail.
    const fitsRoyalMailDimensions = boxes.length > 0 && boxes.every(fitsRoyalMailBox)
    const royalMailWeightLimit = isNorthernIreland ? ROYAL_MAIL_NI_MAX_WEIGHT_KG : ROYAL_MAIL_MAX_WEIGHT_KG
    const totalWeight = boxes.reduce((sum, box) => sum + (box.weight || 0), 0)

    return {
        totalWeight,
        longestSide: getLongestSide(boxes),
        isSingleBox: (boxes.length || 1) === 1,
        isNorthernIreland,
        isDropOff: isDropOffSendType(sendType),
        hasPrinter,
        fitsRoyalMailDimensions,
        fitsRoyalMail: totalWeight <= royalMailWeightLimit && fitsRoyalMailDimensions,
        fitsParcelForce: boxes.length > 0 && boxes.every(fitsParcelForceBox)
    }
}

function route(shipment) {
    if (shipment.totalWeight >= SHIPLY_MIN_WEIGHT_KG || shipment.longestSide > SHIPLY_MIN_LENGTH_CM) {
        return { carrierId: 'shiply', reason: 'Exceeds standard limits (>70kg or >150cm)' }
    }
    if (shipment.isDropOff) return routeDropOff(shipment)
    if (shipment.isNorthernIreland) return routeNorthernIreland(shipment)
    if (shipment.longestSide > OVERSIZED_LENGTH_CM) return routeOversized(shipment)
    if (!shipment.isSingleBox) return routeMultiBox(shipment)
    return routeSingleBox(shipment)
}

/** The customer carries it to a branch, so only the counter services apply. */
function routeDropOff({ fitsRoyalMail, totalWeight, fitsParcelForce }) {
    if (fitsRoyalMail) {
        return { carrierId: 'royal_mail', reason: 'Drop-off within RM limits' }
    }
    if (totalWeight <= PARCELFORCE_MAX_WEIGHT_KG && fitsParcelForce) {
        return { carrierId: 'parcelforce', reason: 'Drop-off within ParcelForce limits' }
    }
    // Not a Shiply booking so much as a dead end: nowhere to drop this off.
    return { carrierId: 'shiply', reason: 'Exceeds drop-off limits (>30kg or >150cm)', notAllowed: true }
}

function routeNorthernIreland({ isSingleBox, fitsRoyalMail, hasPrinter, totalWeight }) {
    if (isSingleBox && fitsRoyalMail) {
        return { carrierId: 'royal_mail', reason: 'NI within RM limits' }
    }
    if (!hasPrinter && totalWeight <= PARCELFORCE_MAX_WEIGHT_KG) {
        return { carrierId: 'parcelforce', reason: 'NI no printer ≤30kg' }
    }
    return { carrierId: 'fedex', reason: 'NI with printer or heavy' }
}

function routeOversized({ hasPrinter, totalWeight, fitsParcelForce }) {
    if (hasPrinter) {
        return { carrierId: 'fedex', reason: 'Oversized with printer' }
    }
    if (totalWeight <= PARCELFORCE_MAX_WEIGHT_KG && fitsParcelForce) {
        return { carrierId: 'parcelforce', reason: 'Oversized no printer ≤30kg' }
    }
    return { carrierId: 'fedex', reason: 'Oversized heavy' }
}

function routeMultiBox({ hasPrinter, totalWeight }) {
    if (hasPrinter) {
        return { carrierId: 'fedex', reason: 'Multi-box with printer' }
    }
    if (totalWeight <= PARCELFORCE_MAX_WEIGHT_KG) {
        return { carrierId: 'parcelforce', reason: 'Multi-box no printer ≤30kg' }
    }
    return { carrierId: 'fedex', reason: 'Multi-box heavy' }
}

function routeSingleBox({ fitsRoyalMail, fitsRoyalMailDimensions, hasPrinter, totalWeight }) {
    if (fitsRoyalMail) {
        return { carrierId: 'royal_mail', reason: 'Within RM limits' }
    }
    if (!hasPrinter && totalWeight <= QR_CODE_MAX_WEIGHT_KG && fitsRoyalMailDimensions) {
        return { carrierId: 'royal_mail', reason: '10-20kg QR code' }
    }
    if (hasPrinter && totalWeight <= QR_CODE_MAX_WEIGHT_KG) {
        return { carrierId: 'fedex', reason: '10-20kg with printer' }
    }
    if (!hasPrinter && totalWeight <= PARCELFORCE_MAX_WEIGHT_KG) {
        return { carrierId: 'parcelforce', reason: '20-30kg no printer' }
    }
    if (hasPrinter && totalWeight <= PARCELFORCE_MAX_WEIGHT_KG) {
        return { carrierId: 'fedex', reason: '20-30kg with printer' }
    }
    if (totalWeight > PARCELFORCE_MAX_WEIGHT_KG) {
        return { carrierId: 'fedex', reason: '>30kg' }
    }
    // Only reachable on a weight that is not a number; FedEx quote it by hand.
    return { carrierId: 'fedex', reason: 'Fallback' }
}

/**
 * Does this `send_type` mean the customer carries it to a branch?
 *
 * The stored HubSpot property is not a clean enum. The worker normalises it for
 * case and for the hyphen in four separate places (`deals.js`). A bare
 * `=== 'dropoff'` would read 'drop-off' as a courier collection and route it
 * down a ladder whose carriers never visit the customer.
 */
function isDropOffSendType(sendType) {
    const normalised = (sendType || '').toLowerCase()
    return normalised === 'dropoff' || normalised === 'drop-off'
}

function fitsRoyalMailBox(box) {
    if (!box) return false
    const [longest, middle, shortest] = [box.length || 0, box.width || 0, box.height || box.depth || 0].sort((a, b) => b - a)
    const [maxLongest, maxMiddle, maxShortest] = ROYAL_MAIL_MAX_DIMS_CM
    return longest <= maxLongest && middle <= maxMiddle && shortest <= maxShortest
}

function fitsParcelForceBox(box) {
    if (!box) return false
    return Math.max(box.length || 0, box.width || 0, box.height || box.depth || 0) <= PARCELFORCE_MAX_LENGTH_CM
}

function getLongestSide(boxes) {
    return boxes.reduce(
        (longest, box) => Math.max(longest, box.length || 0, box.width || 0, box.height || box.depth || 0),
        0
    )
}
